import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Building2, Bot, PhoneCall, CalendarCheck, MessageSquare, CreditCard, Plug, Bell, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/lib/adminApi";
import { StatusBadge } from "@/components/admin/_shared";

const Row = ({ k, v }) => (<div className="flex justify-between gap-4 py-1.5 text-sm"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-900 text-right">{v ?? "—"}</span></div>);
const Section = ({ title, icon: Icon, children, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-5">
    <h2 className="font-heading font-bold tracking-tight flex items-center gap-2 mb-3">{Icon && <Icon className="h-4 w-4 text-blue-600" />}{title}</h2>
    {children}
  </div>
);

export default function AdminCustomerProfile() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { adminApi.get(`/admin/customers/${uid}`).then((r) => setD(r.data)).catch(() => {}); }, [uid]);
  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const { user, business: b, subscription: s, calls, appointments, messages, invoices, integrations, notification_preferences: prefs, phone_setup } = d;
  const connected = Object.entries(integrations || {}).filter(([, v]) => v?.connected).map(([k]) => k);

  return (
    <div data-testid="admin-customer-profile">
      <button onClick={() => navigate("/admin/customers")} className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Back to customers</button>
      <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">{b.name || "—"}</h1>
          <p className="text-slate-500 text-sm">{user.name} · {user.email}</p>
        </div>
        <StatusBadge status={s.status} testid="profile-status" />
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-4">
        <Section title="Business information" icon={Building2} testid="section-business">
          <Row k="Industry" v={b.type} />
          <Row k="Phone" v={b.phone} />
          <Row k="Opening hours" v={b.hours} />
          <Row k="Languages" v={(b.languages || []).join(", ")} />
          <Row k="Services" v={(b.services || []).join(", ")} />
          <div className="pt-2"><p className="text-slate-500 text-sm">Greeting</p><p className="text-sm mt-1 bg-slate-50 rounded-lg p-2">{b.greeting || "—"}</p></div>
        </Section>

        <Section title="AI settings" icon={Bot} testid="section-ai">
          <Row k="AI status" v={b.is_live ? "Live" : (b.vapi_assistant_id ? "Test mode" : "Not created")} />
          <Row k="Voice" v={b.voice} />
          <Row k="Vapi Assistant ID" v={<span className="font-mono text-xs">{b.vapi_assistant_id || "—"}</span>} />
          <Row k="Minutes used" v={`${s.minutes_used || 0}${s.minutes_limit ? " / " + s.minutes_limit : ""}`} />
          <div className="pt-2">
            <p className="text-slate-500 text-sm mb-1">FAQs ({(b.faqs || []).length})</p>
            {(b.faqs || []).slice(0, 4).map((f, i) => <p key={i} className="text-xs text-slate-600 bg-slate-50 rounded p-1.5 mt-1"><b>{f.question}</b> — {f.answer}</p>)}
          </div>
        </Section>

        <Section title="Subscription & billing" icon={CreditCard} testid="section-billing">
          <Row k="Plan" v={s.plan_name || s.plan || "—"} />
          <Row k="Status" v={s.status} />
          <Row k="Trial ends" v={s.trial_ends_at ? new Date(s.trial_ends_at).toLocaleDateString() : "—"} />
          <Row k="Setup" v={s.setup_option === "done_for_you" ? "Done-For-You" : "Self-serve"} />
          <div className="pt-2 space-y-1">
            {invoices.slice(0, 5).map((inv) => <div key={inv.id} className="flex justify-between text-xs"><span className="text-slate-500 truncate">{inv.description}</span><span className="font-semibold">£{inv.amount}</span></div>)}
            {invoices.length === 0 && <p className="text-xs text-slate-400">No invoices.</p>}
          </div>
        </Section>

        <Section title="Phone & integrations" icon={Plug} testid="section-integrations">
          <Row k="Business number" v={phone_setup?.business_number || b.phone} />
          <Row k="AI number" v={phone_setup?.ai_number} />
          <Row k="Connected" v={connected.length ? connected.join(", ") : "None"} />
          <div className="pt-2"><p className="text-slate-500 text-sm flex items-center gap-1"><Bell className="h-3.5 w-3.5" /> Notification prefs</p>
            <p className="text-xs text-slate-600 mt-1">{Object.entries(prefs || {}).filter(([, v]) => v).map(([k]) => k.replace(/_/g, " ")).join(", ") || "Defaults"}</p>
          </div>
        </Section>
      </div>

      <div className="mt-4 grid lg:grid-cols-3 gap-4">
        <Section title={`Call logs (${calls.length})`} icon={PhoneCall} testid="section-calls">
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {calls.slice(0, 20).map((c) => <div key={c.id} className="text-xs border-b border-slate-50 pb-1.5"><p className="font-semibold">{c.caller || "Caller"} · {c.duration || 0}s</p><p className="text-slate-500">{c.summary}</p></div>)}
            {calls.length === 0 && <p className="text-xs text-slate-400">No calls.</p>}
          </div>
        </Section>
        <Section title={`Appointments (${appointments.length})`} icon={CalendarCheck} testid="section-appts">
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {appointments.slice(0, 20).map((a) => <div key={a.id} className="text-xs border-b border-slate-50 pb-1.5"><p className="font-semibold">{a.customer_name}</p><p className="text-slate-500">{a.service} · {a.when}</p></div>)}
            {appointments.length === 0 && <p className="text-xs text-slate-400">No appointments.</p>}
          </div>
        </Section>
        <Section title={`Inbox messages (${messages.length})`} icon={MessageSquare} testid="section-messages">
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {messages.slice(0, 20).map((m) => <div key={m.id} className="text-xs border-b border-slate-50 pb-1.5"><p className="font-semibold">{m.customer_name}</p><p className="text-slate-500">{m.message}</p></div>)}
            {messages.length === 0 && <p className="text-xs text-slate-400">No messages.</p>}
          </div>
        </Section>
      </div>
    </div>
  );
}
